/**
 * Web replacement for react-native-image-zoom-viewer.
 * Provides a simple image carousel with prev/next navigation.
 */
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import EStyleSheet from 'react-native-extended-stylesheet';
import {
    TouchableOpacity,
    View
} from 'react-native-web';

import { Dimensions } from '../../app/common';

const ImageViewer = ({
    imageUrls = [],
    index = 0,
    onChange,
    style,
    enablePreload = true,
    saveToLocalByLongPress,
    flipThreshold,
    renderIndicator,
}) => {
    const [currentIndex, setCurrentIndex] = useState(index);
    const { width, height } = Dimensions.get('window');

    useEffect(() => {
        const idx = Math.max(0, Math.min(index, imageUrls.length - 1));
        setCurrentIndex(idx);
    }, [index, imageUrls.length]);

    const goToPrev = useCallback(() => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            onChange?.(newIndex);
        }
    }, [currentIndex, onChange]);

    const goToNext = useCallback(() => {
        if (currentIndex < imageUrls.length - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            onChange?.(newIndex);
        }
    }, [currentIndex,
        imageUrls.length,
        onChange]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                goToPrev();
            } else if (e.key === 'ArrowRight') {
                goToNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [goToPrev, goToNext]);

    if (!imageUrls?.length) {
        return null;
    }

    const currentImage = imageUrls[currentIndex];
    const imageUrl = currentImage?.url ?? currentImage?.path ?? currentImage?.uri;

    return (
        <View style={[styles.container,
            { width, height },
            style]}>
            <View style={styles.imageWrapper}>
                <img
                    src={imageUrl}
                    alt=""
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        userSelect: 'none',
                    }}
                    draggable={false}
                />
            </View>
            {currentIndex > 0 && (
                <TouchableOpacity
                    style={[styles.navButton, styles.prevButton]}
                    onPress={goToPrev}
                    accessibilityLabel="Previous image"
                >
                    <span style={styles.arrow}>&#8249;</span>
                </TouchableOpacity>
            )}
            {currentIndex < imageUrls.length - 1 && (
                <TouchableOpacity
                    style={[styles.navButton, styles.nextButton]}
                    onPress={goToNext}
                    accessibilityLabel="Next image"
                >
                    <span style={styles.arrow}>&#8250;</span>
                </TouchableOpacity>
            )}
            {renderIndicator?.()}
        </View>
    );
};

const styles = EStyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'black',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    imageWrapper: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
    },
    navButton: {
        position: 'absolute',
        top: '50%',
        marginTop: -30,
        width: 60,
        height: 60,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.4)',
        borderRadius: 30,
        cursor: 'pointer',
        zIndex: 10,
    },
    prevButton: {
        left: 16,
    },
    nextButton: {
        right: 16,
    },
    arrow: {
        color: 'white',
        fontSize: 40,
        lineHeight: 1,
        fontWeight: 300,
    },
});

export default ImageViewer;
